import {
  PATH_CHECKOUT,
  PATH_ORDER_AMENDMENT,
  PATH_PAYMENT,
} from "../../constants/Paths";
import {
  prepareOrder,
  validateAvailableInventory,
} from "../../logic/server/OrderFunction";
import {
  clearRequiredDeliveryCharges,
  clearRequiredExclusivePromotionCharges,
  clearRequiredSurchargeCharges,
  replaceCart,
  updateAgentPolicyManualPricing,
  updateBaseManualPricing,
  updateRequiredDeliveryCharges,
  updateRequiredExclusivePromotionCharges,
  updateRequiredSurchargeCharges,
  updateRouteDetails,
  updateSelectedTimeSlot,
  updateUserProfileDetails,
} from "../kit/Redux/Actions";

export const reviewOrder = async (
  utils,
  user,
  cartItems,
  existingOrderCartItems,
  selectedStore = null
) => {
  const { webApp, queryId, sessionToken, dispatch, navigate } = utils;
  try {
    // Enabling/disabling the button
    webApp.BackButton.hide();
    webApp.MainButton.disable();
    webApp.MainButton.showProgress(false);

    if (queryId) {
      const { isAvailable, updatedCartItems } =
        await validateAvailableInventory(
          sessionToken,
          cartItems,
          existingOrderCartItems,
          selectedStore
        );

      if (!isAvailable) {
        alert(
          "Some items in your cart are unavailable.\n\nPlease check and confirm to proceed anyways."
        );

        dispatch(replaceCart(updatedCartItems));

        navigate(PATH_CHECKOUT, {
          replace: true,
        });

        webApp.MainButton.hideProgress(true);
      } else {
        completeReviewOrder(utils, user, cartItems, selectedStore);
      }
    }
  } catch (error) {
    webApp.MainButton.hideProgress(true);
    webApp.MainButton.enable();
    webApp.BackButton.show();

    console.error("Error sending message:", error);
    // Handle the error gracefully, e.g., show an error message to the user
    alert(`${error}`);
  }
};

const completeReviewOrder = async (utils, user, cartItems, selectedStore) => {
  const { webApp, sessionToken, dispatch, navigate } = utils;
  try {
    const {
      userId,
      agentId,
      customerName,
      customerContact,
      customerAddress,
      timeSlot,
      surchargeChargeList,
      deliveryChargeList,
      exclusivePromotionChargeList,
    } = user;
    
    // Extract the first two digits from the address variable
    const routeCode = customerAddress.substr(0, 2);

    if (!timeSlot) {
      throw new Error("Invalid timeslot.");
    }

    const userDetails = {
      userId: userId,
      agentId: agentId,
    };

    const userProfile = {
      contactname: customerName,
      contactphone: customerContact,
      postalcode: customerAddress,
    };

    const orderProfile = {
      userProfile: userProfile,
      userDetails: userDetails,
      timeSlot: timeSlot,
    };

    // Prior to getting the surcharges, let's just clear it first
    dispatch(clearRequiredDeliveryCharges());
    dispatch(clearRequiredSurchargeCharges());
    // dispatch(clearRequiredConcurrentPromotionCharges());
    dispatch(clearRequiredExclusivePromotionCharges());

    const charges = [];

    if (surchargeChargeList && surchargeChargeList.length > 0) {
      // Let's check for Postal Code Surcharges
      const postalCodeChargeList = surchargeChargeList.filter(
        (item) => item.qualifier.toLowerCase() === "customeraddress"
      );

      if (postalCodeChargeList && postalCodeChargeList.length > 0) {
        const selectedPostalCodeCharge = postalCodeChargeList.find(
          (item) => item.qualifyingvalue === routeCode
        );

        if (selectedPostalCodeCharge) {
          const chargeId = selectedPostalCodeCharge.id;
          const chargeDetailCode = customerAddress;
          const requestCode = "getChargeByCode";
          const requestTitle = process.env.REACT_APP_SURCHARGE_CHARGES;
          const payload = {
            RequestTitle: requestTitle,
            RequestCode: requestCode,
            ChargeId: chargeId,
            ChargeDetailCode: chargeDetailCode,
          };

          if (charges) {
            charges.push(payload);
          }
        }
      }
    }

    if (deliveryChargeList && deliveryChargeList.length > 0) {
      // Let's check for Delivery Charges
      // Let's get the 'Standard' Delivery for now..
      const deliveryType = "Standard";

      const selectedDeliveryCharge = deliveryChargeList.find(
        (item) => item.qualifier.toLowerCase() === "delivery"
      );

      if (selectedDeliveryCharge) {
        const chargeId = selectedDeliveryCharge.id;
        const chargeDetailCode = deliveryType;
        const requestCode = "getChargeByCode";
        const requestTitle = process.env.REACT_APP_DELIVERY_CHARGES;

        const payload = {
          RequestTitle: requestTitle,
          RequestCode: requestCode,
          ChargeId: chargeId,
          ChargeDetailCode: chargeDetailCode,
        };

        if (charges) {
          charges.push(payload);
        }
      }
    }

    if (
      exclusivePromotionChargeList &&
      exclusivePromotionChargeList.length > 0
    ) {
      // Let's check the first type of  promotion
      // More than No Of Items
      const totalQtyInCart = cartItems.reduce(
        (total, item) => total + item.quantity,
        0
      );

      const selectedCharge = exclusivePromotionChargeList.find(
        (item) => item.qualifier.toLowerCase() === "noofitems"
      );

      if (selectedCharge) {
        const chargeId = selectedCharge.id;
        const operator = selectedCharge.qualifyingvalue;
        const chargeDetailCode = totalQtyInCart;
        const requestCode = "getChargesByCodeNOperator";
        const requestTitle = process.env.REACT_APP_EXCLUSIVE_PROMOTION;

        const payload = {
          RequestTitle: requestTitle,
          RequestCode: requestCode,
          ChargeId: chargeId,
          ChargeDetailCode: chargeDetailCode,
          ChargeDetailOperator: operator,
        };

        if (charges) {
          charges.push(payload);
        }
      }
    }

    const orderPreparation = await prepareOrder(
      sessionToken,
      cartItems,
      charges,
      routeCode,
      orderProfile,
      selectedStore
    );

    const { chargeList, referenceRoute, settings } = orderPreparation;

    if (!referenceRoute) {
      throw new Error("No routes were found based on your postal code.");
    }

    const routeId = referenceRoute.routeid;

    if (!routeId) {
      throw new Error("No routes were found based on your postal code.");
    }

    dispatch(updateRouteDetails(referenceRoute));

    if (chargeList && chargeList.length > 0) {
      const exclusivePromotions = chargeList.filter(
        (item) =>
          item.requesttitle === process.env.REACT_APP_EXCLUSIVE_PROMOTION
      );
      const deliveryCharges = chargeList.filter(
        (item) => item.requesttitle === process.env.REACT_APP_DELIVERY_CHARGES
      );
      const surchargeCharges = chargeList.filter(
        (item) => item.requesttitle === process.env.REACT_APP_SURCHARGE_CHARGES
      );

      if (exclusivePromotions) {
        dispatch(updateRequiredExclusivePromotionCharges(exclusivePromotions));
      }

      if (deliveryCharges) {
        dispatch(updateRequiredDeliveryCharges(deliveryCharges));
      }
      if (surchargeCharges) {
        dispatch(updateRequiredSurchargeCharges(surchargeCharges));
      }
    }

    dispatch(updateUserProfileDetails(userProfile));
    dispatch(updateSelectedTimeSlot(timeSlot));
    dispatch(updateBaseManualPricing(settings.enabledBaseManualPricing));
    dispatch(
      updateAgentPolicyManualPricing(settings.enabledAgentPolicyManualPricing)
    );

    navigate(PATH_ORDER_AMENDMENT, {
      replace: true,
    });

    webApp.MainButton.hideProgress(true);
  } catch (error) {
    console.error("Error sending message:", error);
    webApp.MainButton.hideProgress(true);
    webApp.MainButton.enable();
    webApp.BackButton.show();
    // Handle the error gracefully, e.g., show an error message to the user
    alert(`${error}`);
  }
};

export const areCartItemsSame = (newCart, existCart) => {
  if (newCart.length !== existCart.length) return false;

  const createItemMap = (items) =>
    items.reduce((acc, { id, quantity }) => {
      acc[id] = quantity;
      return acc;
    }, {});

  const map1 = createItemMap(newCart);
  const map2 = createItemMap(existCart);

  for (const [id, quantity] of Object.entries(map1)) {
    if (!map2[id] || map2[id] !== quantity) return false;
  }

  return true;
};

export const filterCartQuantityDifferences = (newCart, existCart) => {
  const createItemMap = (items) =>
    items.reduce((acc, { id, quantity }) => {
      acc[id] = quantity;
      return acc;
    }, {});

  const newCartMap = createItemMap(newCart);
  const existCartMap = createItemMap(existCart);

  const additionalItems = newCart.filter(({ id }) => !existCartMap[id]);
  const removedOrReducedItems = existCart.reduce((acc, { id, quantity }) => {
    if (!newCartMap[id]) {
      // Completely removed items
      acc.push({ id, quantity: 0 }); // Quantity set to 0 to indicate removal
    } else if (newCartMap[id] < quantity) {
      // Reduced quantity items
      acc.push({ id, quantity: newCartMap[id] - quantity }); // Negative value indicates reduction
    }
    return acc;
  }, []);

  return { additionalItems, removedOrReducedItems };
};

export const mergeCartsForAmendment = (existingCartItems, newCartItems) => {
  console.log('=== CART MERGING UTILITY ===');
  console.log('Existing cart items:', existingCartItems.map(item => ({ 
    id: item.product_id || item.id, 
    title: item.product_name || item.title || item.name, 
    quantity: item.quantity || item.expectedqty 
  })));
  console.log('New cart items:', newCartItems.map(item => ({ 
    id: item.id, 
    title: item.title || item.name, 
    quantity: item.quantity,
    availableQty: item.availableQty
  })));
  
  // Add alert to see this data
  alert(`MERGE CART DEBUG:\nExisting: ${JSON.stringify(existingCartItems.map(item => ({ 
    id: item.product_id || item.id, 
    title: item.product_name || item.title || item.name, 
    quantity: item.quantity || item.expectedqty 
  })))}\n\nNew: ${JSON.stringify(newCartItems.map(item => ({ 
    id: item.id, 
    title: item.title || item.name, 
    quantity: item.quantity,
    availableQty: item.availableQty
  })))}`);

  // Create a map of existing items for quick lookup (use product_id for existing items)
  const existingItemsMap = existingCartItems.reduce((acc, item) => {
    const itemId = item.product_id || item.id;
    acc[itemId] = {
      ...item,
      id: itemId,
      title: item.product_name || item.title || item.name,
      quantity: parseInt(item.quantity || item.expectedqty || 0),
      price: parseFloat(item.sales_price || item.price || 0),
      category: item.category,
      brand: item.brand,
      type: item.type,
      availableQty: parseInt(item.availableQty || item.availableqty || 0)
    };
    return acc;
  }, {});

  // Create a map of new cart items for quick lookup
  const newCartItemsMap = newCartItems.reduce((acc, item) => {
    acc[item.id] = item;
    return acc;
  }, {});

  // Store original existing items for later filtering (before items get deleted from existingItemsMap)
  const originalExistingItemsMap = { ...existingItemsMap };

  // Start with deep copies of new cart items (to avoid state mutation)
  const mergedCartWithNulls = newCartItems.map(newItem => {
    const existingItem = existingItemsMap[newItem.id];
    
    if (existingItem) {
      // Item exists in both - handle merging carefully
      const newQty = parseInt(newItem.quantity || 0);
      const existingQty = parseInt(existingItem.quantity || 0);
      
      // Special case: If new cart has 0 available but still has this item,
      // it means user wants to keep the existing quantity (not add more)
      // EXCLUDE this item from merged cart entirely (let backend handle existing items)
      if (newItem.availableQty === 0 && newQty > 0) {
        console.log(`Special case: ${newItem.title || newItem.name} has 0 available but ${newQty} in new cart - EXCLUDING from merged cart (unchanged)`);
        
        alert(`SPECIAL CASE TRIGGERED: ${newItem.title || newItem.name}\nNew qty: ${newQty}, Available: ${newItem.availableQty}\nExisting qty: ${existingQty}\nACTION: EXCLUDING from merged cart`);
        
        // Remove from existingItemsMap since it's been processed
        delete existingItemsMap[newItem.id];
        
        // Return null to filter out this item
        return null;
      } else {
        // Normal case: sum the quantities
        const mergedQuantity = newQty + existingQty;
        console.log(`Merged item: ${newItem.title || newItem.name} = ${newQty} (new) + ${existingQty} (existing) = ${mergedQuantity}`);
        
        // Remove from existingItemsMap since it's been processed
        delete existingItemsMap[newItem.id];
        
        // Return new object with merged quantity (avoiding mutation)
        // For available quantity: add the existing item's original reserved amount back
        const adjustedAvailableQty = newItem.availableQty + existingItem.quantity;
        
        return {
          ...newItem,
          quantity: mergedQuantity,
          availableQty: adjustedAvailableQty
        };
      }
    } else {
      // Item only in new cart - return new object copy
      console.log(`New item: ${newItem.title || newItem.name} = ${newItem.quantity} (new only)`);
      return {
        ...newItem,
        quantity: parseInt(newItem.quantity || 0)
      };
    }
  });

  // Filter out null values (excluded unchanged items)
  const mergedCart = mergedCartWithNulls.filter(item => item !== null);

  // Add remaining existing items that weren't in the new cart
  Object.values(existingItemsMap).forEach(existingItem => {
    const formattedExistingItem = {
      id: existingItem.id,
      title: existingItem.title,
      price: existingItem.price,
      category: existingItem.category,
      brand: existingItem.brand,
      type: existingItem.type,
      Image: "NA", // Default for existing items
      availableQty: existingItem.availableQty,
      quantity: existingItem.quantity
    };
    mergedCart.push(formattedExistingItem);
    console.log(`Existing only item: ${existingItem.title} = ${existingItem.quantity} (existing only)`);
  });

  // Filter out items that shouldn't be processed (items with 0 available that aren't actually being changed)
  const validMergedCart = mergedCart.filter(item => {
    const newCartItem = newCartItemsMap[item.id];
    const existingItem = originalExistingItemsMap[item.id];
    
    // If this is an item that exists in both carts with 0 available and same quantity,
    // it means "keep existing, don't add more" - filter it out from validation
    if (newCartItem && existingItem && 
        newCartItem.availableQty === 0 && 
        item.quantity === existingItem.quantity) {
      console.log(`FILTERED OUT unchanged item: ${item.title} (existing: ${existingItem.quantity}, merged: ${item.quantity}, availableQty: ${newCartItem.availableQty})`);
      return false;
    }
    
    // Also filter out items with 0 available that user tried to add (shouldn't happen but safety check)
    if (newCartItem && !existingItem && newCartItem.availableQty === 0 && newCartItem.quantity > 0) {
      console.log(`FILTERED OUT invalid new item: ${item.title} (availableQty: 0, trying to add: ${newCartItem.quantity})`);
      return false;
    }
    
    return true;
  });

  console.log('Merged cart result (after filtering invalid items):', validMergedCart.map(item => ({ 
    id: item.id, 
    title: item.title || item.name, 
    quantity: item.quantity 
  })));
  
  alert(`FINAL MERGED CART RESULT:\n${JSON.stringify(validMergedCart.map(item => ({ 
    id: item.id, 
    title: item.title || item.name, 
    quantity: item.quantity,
    availableQty: item.availableQty
  })), null, 2)}`);
  
  return validMergedCart;
};

export const filterCartDifferencesForNewToExisting = (originalNewCart, existingCart) => {
  console.log('=== FILTER CART DIFFERENCES FOR NEW-TO-EXISTING WORKFLOW ===');
  console.log('Original new cart (what user created):', originalNewCart.map(item => ({ 
    id: item.id, 
    title: item.title || item.name, 
    quantity: item.quantity 
  })));
  console.log('Existing cart (what was already ordered):', existingCart.map(item => ({ 
    id: item.product_id || item.id, 
    title: item.product_name || item.title || item.name, 
    quantity: item.quantity || item.expectedqty 
  })));
  
  // For new-to-existing workflow:
  // MORE section = the original new cart items (what user wants to add)
  // LESS section = empty (we're not removing anything)
  
  const addedOrIncreasedItems = originalNewCart.map(item => ({
    ...item,
    quantity: parseInt(item.quantity || 0)
  }));
  
  const removedOrReducedItems = []; // Always empty for new-to-existing workflow
  
  console.log('New-to-existing workflow results:', {
    addedOrIncreasedItems: addedOrIncreasedItems.length + ' items to add',
    removedOrReducedItems: '0 items (nothing removed)'
  });
  
  return { addedOrIncreasedItems, removedOrReducedItems };
};

export const filterCartDifferences = (newCart, existCart) => {
  console.log('filterCartDifferences called - newCart vs existCart comparison');
  console.log('newCart items:', newCart.length, 'existCart items:', existCart.length);
  console.log('newCart details:', newCart.map(item => ({ id: item.id, title: item.title, quantity: item.quantity })));
  console.log('existCart details:', existCart.map(item => ({ id: item.id, title: item.title, quantity: item.quantity })));
  
  const createItemMap = (items) =>
    items.reduce((acc, item) => {
      acc[item.id] = item; // Store the whole item object instead of just the quantity
      return acc;
    }, {});

  const newCartMap = createItemMap(newCart);
  const existCartMap = createItemMap(existCart);

  // Process removed or reduced items (for LESS section)
  const removedOrReducedItems = existCart.reduce((acc, item) => {
    const { id, quantity } = item;
    if (!newCartMap[id]) {
      // Completely removed items
      acc.push({ ...item, quantity: 0 - existCartMap[id].quantity }); // Include whole item details, set quantity to 0 to indicate removal
      console.log(`Adding REMOVED item: ${item.title || item.name} (completely removed)`);
    } else if (newCartMap[id].quantity < quantity) {
      // Reduced quantity items
      acc.push({ ...item, quantity: newCartMap[id].quantity - quantity }); // Include whole item details, adjust quantity to show the reduction
      console.log(`Adding QUANTITY DECREASE: ${item.title || item.name} ${newCartMap[id].quantity - quantity} (${quantity} → ${newCartMap[id].quantity})`);
    } else if (newCartMap[id].quantity === quantity) {
      // Same quantity - no change, don't add to LESS section
      console.log(`UNCHANGED item: ${item.title || item.name} (${quantity} remains ${quantity})`);
    } else if (newCartMap[id]) {
      // Item exists in both but quantities don't match - debug this
      console.log(`DEBUG: Item ${item.title || item.name} exists in both carts but quantities don't match:`);
      console.log(`- existCart quantity: ${quantity} (type: ${typeof quantity})`);
      console.log(`- newCart quantity: ${newCartMap[id].quantity} (type: ${typeof newCartMap[id].quantity})`);
      console.log(`- Strict equality check: ${newCartMap[id].quantity === quantity}`);
      console.log(`- Loose equality check: ${newCartMap[id].quantity == quantity}`);
    }
    return acc;
  }, []);

  // Process added or increased items (for MORE section)
  const addedOrIncreasedItems = newCart.reduce((acc, item) => {
    const { id, quantity } = item;
    if (existCartMap[id] && quantity > existCartMap[id].quantity) {
      // Increased quantity items
      acc.push({ ...item, quantity: quantity - existCartMap[id].quantity }); // Adjust quantity to show the increase
      console.log(`Adding QUANTITY INCREASE: ${item.title || item.name} +${quantity - existCartMap[id].quantity} (${existCartMap[id].quantity} → ${quantity})`);
    } else if (!existCartMap[id]) {
      // Newly added items
      acc.push({ ...item }); // Include whole item details for newly added items
      console.log(`Adding NEW item: ${item.title || item.name}`);
    } else if (existCartMap[id] && quantity === existCartMap[id].quantity) {
      // Same quantity - no change, don't add to MORE section
      console.log(`UNCHANGED item: ${item.title || item.name} (${quantity} remains ${quantity})`);
    } else if (existCartMap[id]) {
      // Item exists in both but quantities don't match - debug this
      console.log(`DEBUG: Item ${item.title || item.name} exists in both carts but quantities don't match:`);
      console.log(`- newCart quantity: ${quantity} (type: ${typeof quantity})`);
      console.log(`- existCart quantity: ${existCartMap[id].quantity} (type: ${typeof existCartMap[id].quantity})`);
      console.log(`- Strict equality check: ${quantity === existCartMap[id].quantity}`);
      console.log(`- Loose equality check: ${quantity == existCartMap[id].quantity}`);
    }
    return acc;
  }, []);

  console.log('filterCartDifferences results:', {
    addedOrIncreasedItems: addedOrIncreasedItems.length,
    removedOrReducedItems: removedOrReducedItems.length
  });

  return { addedOrIncreasedItems, removedOrReducedItems };
};
